import { useState } from "react";
import { P, pub } from "@/lib/publicApi";
import { L } from "@/components/public/ComplaintForm";
import { toast } from "sonner";
import { Upload, Lock, FileCheck2 } from "lucide-react";

const field = "w-full px-3 py-2.5 text-sm rounded-sm border outline-none bg-white text-[#12263A] placeholder:text-[#94A3B8] focus:border-[#12263A]";

export default function EvidenceUpload() {
  const [caseNo, setCaseNo] = useState("");
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState([]);

  const submit = async () => {
    if (!caseNo.trim() || !code.trim()) { toast.error("Enter your case number and secure access code."); return; }
    if (!files.length) { toast.error("Choose at least one file."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("code", code.trim());
      fd.append("description", desc);
      fd.append("uploader_name", name);
      files.forEach((f) => fd.append("files", f));
      const r = await pub.post(`/cases/${encodeURIComponent(caseNo.trim())}/evidence`, fd);
      setDone((p) => [...p, ...r.data.evidence]);
      setFiles([]);
      toast.success(`${r.data.evidence.length} file(s) securely attached to case ${caseNo.trim()}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed — please try again."); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="flex items-center gap-4">
        <img src="/brand/ia-seal.png" alt="Matcom Internal Affairs seal" data-testid="evidence-ia-seal" className="w-20 h-20 rounded-full shrink-0" />
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.2em]" style={{ color: P.gold }}>Matcom Internal Affairs</div>
          <h2 className="text-2xl font-bold" style={{ color: P.navy }}>Upload Evidence</h2>
          <p className="text-sm mt-1" style={{ color: P.dim }}>Securely upload photos, video, audio, or documents related to an existing case. Every file is hashed (SHA-256), assigned an evidence number, and entered into the case chain of custody.</p>
        </div>
      </div>
      <div className="p-3 rounded-sm flex items-center gap-2 text-xs" style={{ background: "#EEF2F6", border: `1px solid ${P.navy}33`, color: P.navy }}>
        <Lock size={14} /> Evidence must be tied to a case. Enter the case number and access code from your receipt.
      </div>
      <div className="p-4 rounded-sm border space-y-3" style={{ borderColor: P.line, background: "#fff" }}>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><L>Case Number *</L><input data-testid="evidence-case-number" className={field} style={{ borderColor: P.line }} value={caseNo} onChange={(e) => setCaseNo(e.target.value)} placeholder="2026-0001" /></div>
          <div><L>Secure Access Code *</L><input data-testid="evidence-access-code" className={`${field} font-mono`} style={{ borderColor: P.line }} value={code} onChange={(e) => setCode(e.target.value)} placeholder="A1B2C3D4" /></div>
        </div>
        <div><L>Your Name</L><input data-testid="evidence-uploader-name" className={field} style={{ borderColor: P.line }} value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name (optional if anonymous case)" /></div>
        <div><L>Description of Files</L><input data-testid="evidence-description" className={field} style={{ borderColor: P.line }} value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Briefly describe what you are submitting" /></div>
        <div>
          <L>Files (photos, video, audio, PDF, Word — max 50MB each, 5 per upload)</L>
          <input data-testid="evidence-file-input" type="file" multiple
            accept=".jpg,.jpeg,.png,.webp,.pdf,.mp4,.mov,.mp3,.wav,.m4a,.doc,.docx,.txt"
            onChange={(e) => setFiles(Array.from(e.target.files || []).slice(0, 5))}
            className="w-full text-sm py-2" />
          {files.length > 0 && <div className="text-xs mt-1" style={{ color: P.dim }}>{files.map((f) => f.name).join(" · ")}</div>}
        </div>
        <button data-testid="evidence-submit" disabled={busy} onClick={submit}
          className="w-full py-3 text-sm font-bold text-white rounded-sm flex items-center justify-center gap-2 hover:opacity-90 disabled:opacity-50" style={{ background: P.navy }}>
          <Upload size={15} /> {busy ? "UPLOADING SECURELY…" : "SUBMIT FILES SECURELY"}
        </button>
      </div>
      {done.length > 0 && (
        <div data-testid="evidence-done-list" className="p-4 rounded-sm border space-y-2" style={{ borderColor: P.green, background: "#EDF5EF" }}>
          <div className="text-xs font-bold uppercase tracking-wider flex items-center gap-1.5" style={{ color: P.green }}><FileCheck2 size={14} /> Evidence Received</div>
          {done.map((e) => (
            <div key={e.eid} className="text-sm" style={{ color: P.ink }}>
              <span className="font-mono font-bold">{e.eid}</span> — {e.filename}
              <div className="font-mono text-[10px] break-all" style={{ color: P.dim }}>SHA-256: {e.sha256}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
